import { Group, Stack } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import { PaginationState } from "@tanstack/react-table"
import { DEFAULT_PAGE_SIZE, InfoBox } from "@tfg/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { RDUEComplianceStatus } from "@tfg/types/schemas/enum"
import { filtersRdueSchema } from "@tfg/types/schemas/products"

import { DataFetchWrapper } from "~/components/DataFetchWrapper"
import { InlineBanner } from "~/components/InlineBanner"
import { ProductFilters } from "~/components/ProductFilters.tsx"
import { ProductSearch } from "~/components/ProductSearch"
import { useSearchParamsFilters } from "~/components/hooks/useSearchParamsFilters"
import { ProductsRDUETAble } from "~/pages/Brand/Regulations/RDUE/ProductsRDUETable.tsx"
import { useProductsRDUEQuery } from "~/pages/Brand/Regulations/RDUE/hooks.ts"
import { useAuth } from "~/store/auth-context.tsx"

export const ProductsRDUEWrapper = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  const [filtersValue, setFiltersValue] =
    useSearchParamsFilters(filtersRdueSchema)

  const [debouncedProduct] = useDebouncedValue<string | undefined>(
    filtersValue.search,
    600
  )

  const productsQuery = useProductsRDUEQuery({
    fetchDataOptions: pagination,
    filters: { ...filtersValue, search: debouncedProduct },
  })

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }))
  }, [debouncedProduct])

  const { t } = useTranslation("regulations")
  const { user, isAdmin } = useAuth()
  let adminMessage
  if (user?.organization?.settings?.rduePlan === "DISABLED") {
    if (isAdmin) {
      adminMessage =
        "You are seeing the content of this page because you are an admin. Non-admin user see a message explaining that they need to upgrade their plan to see this content. Go to the organization settings page to change the plan."
    } else {
      return (
        <InfoBox
          title={t("notSubscribed.title")}
          message={t("notSubscribed.message")}
          variant="info"
        />
      )
    }
  }

  return (
    <Stack>
      {adminMessage && (
        <InlineBanner type="admin" size="xs" message={t("notSubscribed.title")}>
          {adminMessage}
        </InlineBanner>
      )}
      <DataFetchWrapper query={productsQuery}>
        {({ data, isFetching }) => (
          <Stack>
            <Group>
              <ProductSearch
                filtersValue={filtersValue}
                setFiltersValue={setFiltersValue}
                isFetching={isFetching}
              />
              <ProductFilters
                setFiltersValue={setFiltersValue}
                filtersValue={filtersValue}
                extraFilters={[
                  {
                    label: t("lists.placeholders.rdueStatus"),
                    key: "rdueCompliance",
                    type: "select",
                    options: Object.entries(RDUEComplianceStatus).map(
                      ([key, value]) => ({
                        value: key,
                        label: value,
                      })
                    ),
                  },
                ]}
              />
            </Group>

            <ProductsRDUETAble
              data={data}
              fetchDataOptions={pagination}
              setPagination={setPagination}
            />
          </Stack>
        )}
      </DataFetchWrapper>
    </Stack>
  )
}
